import {
  type ComputedRef,
  computed,
  getCurrentInstance,
  type InjectionKey,
  type MaybeRefOrGetter,
  nextTick,
  onScopeDispose,
  provide,
  type Ref,
  ref,
  toValue,
  watch,
} from 'vue';
import { type Router, useRoute, useRouter } from 'vue-router';
import type {
  ActiveFilterModelValue,
  Filter,
  SearchTextFilterProps,
  TextSearchOption,
} from '../store';
import { useFiltersStore } from '../store/filters.store';
import {
  FilterTypes,
  getDefaultSearchByField,
  getFilterQueryKey,
  getFilterReadiness,
  getNeedleByQueryKey,
  getOwnedQueryKeys,
  getQueryKey,
  hasCompactFilters,
  isSyncableFilter,
  mergeOwnedQuery,
  parseFilterValue,
  requestFilterOptions,
  resolveSearchFieldName,
  serializeFiltersToQuery,
  stringifyOwnedQuery,
} from '../utils';
import { wereFiltersReadForTable } from './filters-table-reads';
import { useFilters } from './use-filters';

type QueryValue = string | null;

type ReplaceOutcome = Awaited<ReturnType<Router['replace']>>;

type QueryRecord = Record<string, QueryValue | QueryValue[]>;

export type UseFiltersQuerySyncOptions = {
  filterNamespace: string;
  filtersConfig: MaybeRefOrGetter<Filter[] | undefined>;
  searchFieldName?: string;
  searchByOptions?: MaybeRefOrGetter<TextSearchOption[] | undefined>;
  enabled?: MaybeRefOrGetter<boolean>;
  paramPrefix?: string;
  syncTextSearch?: boolean;
  compactFilters?: boolean;
};

export type UseFiltersQuerySync = {
  hydrated: Readonly<Ref<boolean>>;
  hydratedFromQuery: Readonly<Ref<boolean>>;
  ownedQueryKeys: Readonly<Ref<string[]>>;
  pendingFilterFields: Readonly<Ref<string[]>>;
  stop: () => void;
};

const searchByOptionsKeys = new Map<
  string,
  InjectionKey<ComputedRef<TextSearchOption[] | undefined>>
>();

export const getSearchByOptionsInjectionKey = (filterNamespace: string) => {
  let key = searchByOptionsKeys.get(filterNamespace);
  if (!key) {
    key = Symbol(`filters-query-sync:search-by:${filterNamespace}`);
    searchByOptionsKeys.set(filterNamespace, key);
  }
  return key;
};

/*
  Dev-only diagnostics: no runtime branch reads it. It surfaces in review the
  routes that host more than one namespace without paramPrefix, where two
  filters with the same name would compete for the same query key.
*/
const devNamespacesByRoute = new Map<string, Set<string>>();

const warnOnSharedRouteOwnership = (
  routeKey: string,
  filterNamespace: string,
) => {
  const namespaces = devNamespacesByRoute.get(routeKey) ?? new Set<string>();
  namespaces.add(filterNamespace);
  devNamespacesByRoute.set(routeKey, namespaces);

  if (namespaces.size < 2) return;

  console.warn(
    `useFiltersQuerySync: the route "${routeKey}" hosts more than one filter namespace (${[
      ...namespaces,
    ].join(', ')}) without paramPrefix: the query params can collide.`,
  );
};

const isPresentQueryValue = (value: QueryValue | QueryValue[] | undefined) =>
  typeof value === 'string' ||
  (Array.isArray(value) && value.some((entry) => typeof entry === 'string'));

const firstQueryValue = (
  value: QueryValue | QueryValue[] | undefined,
): string | undefined => {
  const first = Array.isArray(value) ? value[0] : value;
  return typeof first === 'string' ? first : undefined;
};

const pickOwned = (
  query: QueryRecord,
  ownedKeys: string[],
): Record<string, string> => {
  const owned: Record<string, string> = {};
  for (const key of ownedKeys) {
    const value = firstQueryValue(query[key]);
    if (value !== undefined && value !== '') {
      owned[key] = value;
    }
  }
  return owned;
};

/*
  Hydration runs synchronously in the composable body, not in onMounted: the
  page creates the fetch composable right after this call and captures its
  initialParams in the same tick. Hydrating after mount would send the first
  request without filters, and the URL values arriving later would cause a
  second one, with a flash of the unfiltered list.
*/
export function useFiltersQuerySync(
  options: UseFiltersQuerySyncOptions,
): UseFiltersQuerySync {
  const {
    filterNamespace,
    filtersConfig,
    searchFieldName,
    searchByOptions,
    enabled,
    paramPrefix,
    syncTextSearch,
    compactFilters,
  } = options;

  const hydrated = ref(false);
  const hydratedFromQuery = ref(false);
  const pendingFilterFields = ref<string[]>([]);

  const router = useRouter();
  const route = useRoute();

  const config = computed(() => toValue(filtersConfig));
  const codecOptions = { searchFieldName, paramPrefix, syncTextSearch };

  const ownedQueryKeys = computed(() =>
    router && route && filterNamespace
      ? getOwnedQueryKeys(config.value, codecOptions)
      : [],
  );

  const noop = () => undefined;

  /*
    Without a namespace the store has nowhere to write: claiming the keys anyway
    would remove them from the URL without applying anything.
  */
  if (!router || !route || !filterNamespace) {
    hydrated.value = true;
    return {
      hydrated,
      hydratedFromQuery,
      ownedQueryKeys,
      pendingFilterFields,
      stop: noop,
    };
  }

  if (import.meta.env.DEV && wereFiltersReadForTable(filterNamespace)) {
    console.warn(
      `useFiltersQuerySync: "${filterNamespace}" was read for a request before the URL sync. Call useFiltersQuerySync before the fetch composable, otherwise the first request goes out without the URL filters.`,
    );
  }

  const filters = useFilters(filterNamespace);
  const allowedSearchBy = computed(() => toValue(searchByOptions));

  /*
    The "search by" options are declared only here: the table top bar receives
    them through inject, so URL, store and select cannot drift apart.
  */
  if (searchByOptions !== undefined && getCurrentInstance()) {
    provide(getSearchByOptionsInjectionKey(filterNamespace), allowedSearchBy);
  }

  const isEnabled = computed(() => toValue(enabled) !== false);
  const mountedRouteName = route.name;

  const searchFieldKey = computed(() =>
    resolveSearchFieldName(config.value, searchFieldName),
  );

  const syncableFilters = computed(() =>
    (config.value ?? []).filter(isSyncableFilter),
  );

  const isCompact = computed(() =>
    hasCompactFilters(config.value, compactFilters),
  );

  let confirmedOwned: string | undefined;
  let pendingOwned: string | undefined;
  let writeGeneration = 0;
  let retriedDesired: string | undefined;
  let stopped = false;

  /*
    URL values that have not entered the store yet, kept out of it so they do
    not reach the API before validation. They stay in the canonical
    projection, so the URL rewrite does not erase them:
    - held: closed sets whose options have not arrived yet, applied as soon as
      they become decidable;
    - frozen: keys that entered the config after mount, never applied, because
      applying a filter by surprise while the user already works on the list
      is worse than not applying it.
  */
  const pendingRawValues = new Map<string, string>();
  const frozenRawValues = new Map<string, string>();
  let previousSyncableFields = new Set<string>();

  const syncPendingFilterFields = () => {
    pendingFilterFields.value = [...pendingRawValues.keys()];
  };

  const conservedRawValues = (): ReadonlyMap<string, string> => {
    if (frozenRawValues.size === 0) return pendingRawValues;
    const merged = new Map(pendingRawValues);
    for (const [fieldName, raw] of frozenRawValues) {
      merged.set(fieldName, raw);
    }
    return merged;
  };

  const forgetConservedValue = (fieldName: string) => {
    pendingRawValues.delete(fieldName);
    frozenRawValues.delete(fieldName);
  };

  const currentQuery = () => route.query as QueryRecord;

  const desiredOwnedQuery = () =>
    serializeFiltersToQuery(config.value, filters.getFiltersForTable.value, {
      ...codecOptions,
      searchByOptions: allowedSearchBy.value,
      pendingRawValues: conservedRawValues(),
    });

  const currentOwnedIdentity = () =>
    stringifyOwnedQuery(pickOwned(currentQuery(), ownedQueryKeys.value));

  /*
    The marker is realigned to what the route really contains instead of being
    restored to a previous value: claiming the URL contains something it does
    not would classify an external navigation as "our echo". The retry is tied
    to the desired value and not to the generation, because every retry opens a
    new one: counting it per generation would make it unbounded against a
    guard that always blocks.
  */
  const onWriteFailed = (desired: string) => {
    if (stopped) return;
    pendingOwned = undefined;
    confirmedOwned = currentOwnedIdentity();
    if (retriedDesired === desired) return;
    retriedDesired = desired;
    void nextTick(reconcile);
  };

  function reconcile() {
    if (stopped) return;
    if (!isEnabled.value || ownedQueryKeys.value.length === 0) return;
    if (route.name !== mountedRouteName) return;

    const desiredOwned = desiredOwnedQuery();
    const desired = stringifyOwnedQuery(desiredOwned);
    /*
      The comparison is against the real route.query and not an internal
      marker: it lets the sync both publish the filters restored from the
      session at startup and fix a non-canonical URL, cases where a memo of its
      own last write would say there is nothing to do.
    */
    if (desired === currentOwnedIdentity()) return;

    const generation = ++writeGeneration;
    pendingOwned = desired;

    void router
      .replace({
        query: mergeOwnedQuery(
          currentQuery(),
          desiredOwned,
          ownedQueryKeys.value,
        ),
      })
      .then((failure: ReplaceOutcome) => {
        if (generation !== writeGeneration) return;
        if (!failure) {
          confirmedOwned = desired;
          pendingOwned = undefined;
          retriedDesired = undefined;
          return;
        }
        onWriteFailed(desired);
      })
      .catch(() => {
        if (generation !== writeGeneration) return;
        onWriteFailed(desired);
      });
  }

  /*
    The actions emitted from here must not be read as user interaction:
    otherwise they would erase the held values they just recorded.
  */
  let applyingInternally = false;

  const withInternalApply = (apply: () => void) => {
    applyingInternally = true;
    try {
      apply();
    } finally {
      applyingInternally = false;
    }
  };

  const applyHydratedValue = (
    filter: Filter,
    value: ActiveFilterModelValue,
  ): boolean => {
    /*
      A filter that the URL leaves empty and that is not in the namespace yet is
      not materialized: being in the store alone would show it as an active
      chip and remove it from the filter selector.
    */
    if (
      value === undefined &&
      !filters.filterIsActive(filter.filterFieldName)
    ) {
      return false;
    }

    /*
      Outside the compact bar an empty chip opens its own dropdown and takes
      focus, so a filter the URL leaves empty is removed from the namespace.
      The compact bar keeps it with an undefined value, because its filters
      must stay mounted.
    */
    if (value === undefined && !isCompact.value) {
      withInternalApply(() => filters.removeFilter(filter.filterFieldName));
      return false;
    }

    withInternalApply(() => {
      /*
        upsertFilter runs before applyFilterValue because the store drops the
        value with a warning when the filter is not in the namespace yet; the
        upsert is idempotent and keeps the existing modelValue for the same
        filterType, so it does not collide with the top bar seeding.
      */
      if (value !== undefined) {
        filters.upsertFilter({ ...filter, modelValue: undefined } as Filter);
      }

      filters.applyFilterValue({
        filterFieldName: filter.filterFieldName,
        filterType: filter.filterType,
        value,
        fromKey: filter.fromKey,
        toKey: filter.toKey,
      });
    });

    return value !== undefined;
  };

  /*
    A needleBy that is missing or no longer allowed falls back to the default
    option: it is the one the select shows, and the request must search on the
    same field.
  */
  const resolveNeedleBy = (candidate: string | undefined) => {
    const allowed = allowedSearchBy.value;
    if (!allowed?.length) return undefined;
    return allowed.some((option) => option.fieldName === candidate)
      ? candidate
      : getDefaultSearchByField(allowed);
  };

  const sanitizeStoredNeedleBy = () => {
    const allowed = allowedSearchBy.value;
    if (!allowed?.length) return;

    const searchFilter = filters.getFilters.value?.find(
      (filter) => filter.filterType === FilterTypes.TEXT_SEARCH,
    ) as SearchTextFilterProps | undefined;
    if (!searchFilter?.modelValue) return;

    const current = searchFilter.selectedSearchBy;
    const resolved = resolveNeedleBy(current);
    if (resolved === current) return;

    withInternalApply(() =>
      filters.applyFilterValue({
        filterFieldName: searchFilter.filterFieldName,
        filterType: FilterTypes.TEXT_SEARCH,
        value: searchFilter.modelValue,
        needleBy: resolved,
      }),
    );
  };

  const hydrateSearch = (query: QueryRecord): boolean => {
    if (syncTextSearch !== true) return false;

    const searchFilter =
      config.value?.find(
        (filter) => filter.filterType === FilterTypes.TEXT_SEARCH,
      ) ??
      ({
        filterType: FilterTypes.TEXT_SEARCH,
        filterFieldName: searchFieldKey.value,
        filterLabel: FilterTypes.TEXT_SEARCH,
      } as Filter);

    const rawNeedle = query[getQueryKey(searchFieldKey.value, paramPrefix)];
    const parsed = isPresentQueryValue(rawNeedle)
      ? parseFilterValue(searchFilter, rawNeedle)
      : { ok: true as const, value: undefined };
    const needle = parsed.ok ? (parsed.value as string | undefined) : undefined;

    const needleBy = needle
      ? resolveNeedleBy(
          firstQueryValue(query[getNeedleByQueryKey(paramPrefix)])?.trim(),
        )
      : undefined;

    const hasSearchInStore = filters.getFilters.value?.some(
      (filter) => filter.filterType === FilterTypes.TEXT_SEARCH,
    );

    if (needle === undefined && !hasSearchInStore) return false;

    withInternalApply(() =>
      filters.applyFilterValue({
        filterFieldName: searchFieldKey.value,
        filterType: FilterTypes.TEXT_SEARCH,
        value: needle,
        needleBy,
      }),
    );

    return needle !== undefined;
  };

  const hydrate = (query: QueryRecord): boolean => {
    const hasOwnedParam = ownedQueryKeys.value.some((key) =>
      isPresentQueryValue(query[key]),
    );
    if (!hasOwnedParam) return false;

    let appliedFromQuery = false;

    const queryOrder = Object.keys(query);
    const queryRank = (filter: Filter) => {
      const index = queryOrder.indexOf(getFilterQueryKey(filter, paramPrefix));
      return index === -1 ? queryOrder.length : index;
    };
    const inQueryOrder = [...syncableFilters.value].sort(
      (a, b) => queryRank(a) - queryRank(b),
    );

    for (const filter of inQueryOrder) {
      const raw = query[getFilterQueryKey(filter, paramPrefix)];
      const isPresent = isPresentQueryValue(raw);

      /*
        A closed set without options is not decided "invalid": the value is
        held, otherwise the param would leave the URL before it could be
        validated and the deep link would be lost. The value already in the
        store is cleared anyway: it comes from the session of whoever opens the
        link, and the first request must not filter by a value the URL replaced.
      */
      if (isPresent && getFilterReadiness(filter) === 'pending') {
        pendingRawValues.set(
          filter.filterFieldName,
          firstQueryValue(raw) ?? '',
        );
        requestFilterOptions(filter);
        applyHydratedValue(filter, undefined);
        continue;
      }

      forgetConservedValue(filter.filterFieldName);

      const parsed = isPresent
        ? parseFilterValue(filter, raw)
        : { ok: true as const, value: undefined };
      /*
        Filters the URL does not mention are cleared because, when the URL
        carries at least one param we own, it defines the whole namespace state:
        inheriting the rest from the session would make a shared link depend on
        who opens it.
      */
      const value = parsed.ok ? parsed.value : undefined;

      if (applyHydratedValue(filter, value)) appliedFromQuery = true;
    }

    if (hydrateSearch(query)) appliedFromQuery = true;

    return appliedFromQuery;
  };

  if (isEnabled.value && ownedQueryKeys.value.length > 0) {
    hydratedFromQuery.value = hydrate(currentQuery());
    sanitizeStoredNeedleBy();
  }
  if (import.meta.env.DEV && !paramPrefix) {
    warnOnSharedRouteOwnership(
      String(mountedRouteName ?? route.path),
      filterNamespace,
    );
  }

  hydrated.value = true;
  syncPendingFilterFields();
  previousSyncableFields = new Set(
    syncableFilters.value.map((filter) => filter.filterFieldName),
  );
  confirmedOwned = currentOwnedIdentity();
  reconcile();

  /*
    A held value gets a single chance: as soon as the filter becomes decidable
    it is applied or dropped for good, and from then on the canonical rewrite
    can rightfully remove the param.
  */
  const resolvePendingValues = () => {
    if (pendingRawValues.size === 0) return;

    let resolved = false;

    for (const [fieldName, raw] of [...pendingRawValues]) {
      const filter = syncableFilters.value.find(
        (entry) => entry.filterFieldName === fieldName,
      );
      if (!filter || getFilterReadiness(filter) === 'pending') continue;

      pendingRawValues.delete(fieldName);
      resolved = true;

      const parsed = parseFilterValue(filter, raw);
      if (parsed.ok && parsed.value !== undefined) {
        applyHydratedValue(filter, parsed.value);
      }
    }

    if (!resolved) return;
    syncPendingFilterFields();
    reconcile();
  };

  /*
    A key that enters the config after mount and that the URL already contains
    is not hydrated (§5.6): its raw value is frozen only to keep the canonical
    rewrite from erasing it.
  */
  const freezeNewOwnedFields = () => {
    const query = currentQuery();
    const desired = desiredOwnedQuery();
    const currentFields = new Set<string>();

    for (const filter of syncableFilters.value) {
      const fieldName = filter.filterFieldName;
      currentFields.add(fieldName);
      if (previousSyncableFields.has(fieldName)) continue;

      const queryKey = getFilterQueryKey(filter, paramPrefix);
      const raw = firstQueryValue(query[queryKey]);
      if (raw === undefined || raw === '') continue;
      if (desired[queryKey] !== undefined) continue;

      frozenRawValues.set(fieldName, raw);
    }

    previousSyncableFields = currentFields;
  };

  const stopHandles: Array<() => void> = [
    /*
      Re-enabling the sync is the same as mounting it now: the URL is read again
      and the state is published again, because during the pause both can have
      changed with nobody reconciling them.
    */
    watch(
      isEnabled,
      (nowEnabled) => {
        if (!nowEnabled) return;
        hydratedFromQuery.value = hydrate(currentQuery());
        syncPendingFilterFields();
        confirmedOwned = currentOwnedIdentity();
        reconcile();
      },
      { flush: 'post' },
    ),
    watch(
      () =>
        syncableFilters.value
          .map(
            (filter) =>
              `${filter.filterFieldName}:${getFilterReadiness(filter)}`,
          )
          .join('|'),
      () => {
        if (!isEnabled.value) return;
        freezeNewOwnedFields();
        confirmedOwned = currentOwnedIdentity();
        resolvePendingValues();
        reconcile();
      },
      { flush: 'post' },
    ),
    watch(
      () => stringifyOwnedQuery(desiredOwnedQuery()),
      () => reconcile(),
      {
        flush: 'post',
      },
    ),
    watch(
      () => currentOwnedIdentity(),
      (incoming) => {
        if (!isEnabled.value || ownedQueryKeys.value.length === 0) return;
        if (route.name !== mountedRouteName) return;
        /*
          The comparison includes pendingOwned as well as confirmedOwned
          because the route change event comes before the replace promise
          resolves: without it our own write would be read as an external
          navigation and would hydrate the store again.
        */
        if (incoming === confirmedOwned || incoming === pendingOwned) return;
        hydrate(currentQuery());
        confirmedOwned = incoming;
        pendingOwned = undefined;
        reconcile();
      },
      { flush: 'post' },
    ),
  ];

  /*
    The value kept for a field drops as soon as the user touches it: from then
    on the interaction rules, not the URL the user landed on.
  */
  stopHandles.push(
    useFiltersStore().$onAction(({ name, args }) => {
      if (applyingInternally) return;
      if (pendingRawValues.size === 0 && frozenRawValues.size === 0) return;

      if (name === 'applyFilterValue') {
        const payload = args[0] as {
          entityName?: string;
          filterFieldName?: string;
        };
        if (payload?.entityName !== filterNamespace) return;
        if (!payload.filterFieldName) return;
        forgetConservedValue(payload.filterFieldName);
        syncPendingFilterFields();
        return;
      }

      if (name === 'removeFilter') {
        const [entityName, filterFieldName] = args as [string, string];
        if (entityName !== filterNamespace) return;
        forgetConservedValue(filterFieldName);
        syncPendingFilterFields();
        return;
      }

      if (name === 'initializeFilter') {
        const [entityName] = args as [string];
        if (entityName !== filterNamespace) return;
        pendingRawValues.clear();
        frozenRawValues.clear();
        syncPendingFilterFields();
      }
    }),
  );

  const stop = () => {
    stopped = true;
    writeGeneration += 1;
    while (stopHandles.length > 0) {
      stopHandles.pop()?.();
    }
  };

  onScopeDispose(stop, true);

  return {
    hydrated,
    hydratedFromQuery,
    ownedQueryKeys,
    pendingFilterFields,
    stop,
  };
}
